import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

function Logo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: '2rem' }}>
      <svg width="38" height="38" viewBox="0 0 120 120">
        <defs>
          <linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E85D04"/>
            <stop offset="100%" stopColor="#F48C06"/>
          </linearGradient>
        </defs>
        <path d="M20 30 C20 20, 30 10, 50 10 L70 10 C90 10, 100 20, 100 30 L100 60 C100 70, 90 80, 70 80 L40 80 L20 95 L20 80 C20 80, 20 70, 20 60 Z" fill="url(#lg)" opacity="0.9"/>
        <rect x="40" y="35" width="40" height="4" fill="white" rx="2"/>
        <rect x="42" y="39" width="4" height="25" fill="white" rx="2"/>
        <rect x="74" y="39" width="4" height="25" fill="white" rx="2"/>
        <rect x="38" y="64" width="44" height="6" fill="white" rx="3"/>
        <circle cx="85" cy="25" r="8" fill="#0A6847"/>
        <path d="M85 20 L85 30 M80 25 L90 25" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <span style={{ fontFamily: 'Fraunces, serif', fontSize: '1.3rem', fontWeight: 900, color: 'var(--ink)' }}>
        Table<span style={{ color: 'var(--orange)' }}>Chat</span>
      </span>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [done, setDone]           = useState(false);
  const [validSession, setValidSession] = useState(null); // null=checking, true, false

  useEffect(() => {
    // Supabase automatically handles the token from the URL hash
    // onAuthStateChange fires with PASSWORD_RECOVERY event when token is valid
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          setValidSession(true);
        } else if (event === 'SIGNED_IN' && session) {
          setValidSession(true);
        }
      }
    );

    // Fallback: if no event fires after 3s, check session directly
    const timeout = setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setValidSession(true);
      } else {
        setValidSession(false);
      }
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (err) {
      setError(err.message);
    } else {
      setDone(true);
      // Auto-redirect to dashboard after 3 seconds
      setTimeout(() => navigate('/'), 3000);
    }
  };

  // Checking if the reset token is valid
  if (validSession === null) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <Logo />
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 32, height: 32, border: '2px solid var(--border)', borderTopColor: 'var(--orange)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }}/>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (validSession === false) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <Logo />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.5rem', marginBottom: '0.8rem' }}>
              Link expired
            </h2>
            <p style={{ color: 'var(--mid)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '2rem' }}>
              This password reset link has expired or already been used.
              Request a new one below.
            </p>
            <Link to="/forgot-password" style={{
              display: 'block', background: 'var(--orange)', color: 'white',
              padding: '0.85rem', borderRadius: 8, textDecoration: 'none',
              fontWeight: 700, textAlign: 'center'
            }}>
              Request new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <Logo />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.5rem', marginBottom: '0.8rem' }}>
              Password updated
            </h2>
            <p style={{ color: 'var(--mid)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
              Your password has been changed successfully.
              Redirecting you to your dashboard...
            </p>
            <Link to="/" style={{
              display: 'block', background: 'var(--orange)', color: 'white',
              padding: '0.85rem', borderRadius: 8, textDecoration: 'none',
              fontWeight: 700, textAlign: 'center'
            }}>
              Go to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Logo />
        <h1 style={styles.title}>Set new password</h1>
        <p style={styles.sub}>Choose a strong password for your account.</p>

        <form onSubmit={handleReset} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>New password</label>
            <input
              type="password" value={password} required minLength={8}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              style={styles.input}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm new password</label>
            <input
              type="password" value={confirm} required
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat new password"
              style={styles.input}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={loading} style={styles.btn}>
            {loading ? 'Updating...' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page:  { minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' },
  card:  { background: 'white', borderRadius: 16, padding: '2.5rem', width: '100%', maxWidth: 420, border: '1px solid var(--border)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  title: { fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 700, textAlign: 'center', marginBottom: '0.4rem', color: 'var(--ink)' },
  sub:   { textAlign: 'center', color: 'var(--mid)', fontSize: '0.875rem', marginBottom: '2rem' },
  form:  { display: 'flex', flexDirection: 'column', gap: '1.2rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)' },
  input: { padding: '0.8rem 1rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.95rem', outline: 'none', color: 'var(--ink)', background: 'white' },
  error: { background: 'var(--red-bg)', color: 'var(--red-tx)', padding: '0.75rem 1rem', borderRadius: 8, fontSize: '0.875rem', fontWeight: 500 },
  btn:   { background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.9rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', marginTop: '0.4rem' },
};
